import React, { useState, useEffect, useContext, useRef } from "react";
import "./Profile.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory } from "react-router-dom";
import { Avatar, Button, CircularProgress, Typography } from "@mui/material";
import useProfileQuery from "../../store/Profile/useProfileQuery";
import useBusinessQuery from "../../store/Bussiness/useBusinessQuery";
import useCommonQuery from "../../store/common/useCommonQuery";
import OtpModal from "./OtpModal";
import ReactGoogleAutocomplete from "react-google-autocomplete";
import { useQueryClient } from "react-query";
import { variableContext } from "../../context/VariableContex";
import { uploadFileToS3 } from "../../utils/s3Upload";
import CategoryModal from "../../components/CategoryModal/Index";
import SubCategoryModal from "../../components/SubCategoryModal/Index";
import PersonIcon from "@mui/icons-material/PersonOutline";
import PhoneIcon from "@mui/icons-material/PhoneAndroidOutlined";
import StoreIcon from "@mui/icons-material/StorefrontOutlined";
import CategoryIcon from "@mui/icons-material/CategoryOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOnOutlined";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const API_KEY = "AIzaSyDMC5uUfcSScgVY9AGqsGQwYm8P6_LmyYg";

const Profile = () => {
  const { locationData, setLocationFilter } = useContext(variableContext);
  const history = useHistory();
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  // ── Profile state ──
  const [profileImage, setProfileImage] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [nameError, setNameError] = useState("");

  // ── Business state ──
  const [businessId, setBusinessId] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [categoryImage, setCategoryImage] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [subCategoryName, setSubCategoryName] = useState("");
  const [subCategoryImage, setSubCategoryImage] = useState("");
  const [district, setDistrict] = useState("");
  const [stateId, setStateId] = useState(1);
  const [postOffice, setPostOffice] = useState("");
  const [locationInfo, setLocationInfo] = useState("");
  const [locationDisplay, setLocationDisplay] = useState("");
  const [subCategoryError, setSubCategoryError] = useState("");

  // ── UI state ──
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [categoryModal, setCategoryModal] = useState(false);
  const [subCategoryModal, setSubCategoryModal] = useState(false);
  const [showPending, setShowPending] = useState(false);

  // ── Queries ──
  const { data: profileData, isSuccess: profileSuccess } = useProfileQuery.Get_profile();
  const { mutateAsync: updateProfile } = useProfileQuery.Update_profile();
  const { data: businessList, isSuccess: bizSuccess } = useBusinessQuery.Get_my_bussiness();
  const listBusinessId = businessList?.data?.results?.[0]?.id || null;
  const { data: businessDetail } = useBusinessQuery.Get_my_bussiness_details(listBusinessId);
  const { mutateAsync: editBusiness } = useBusinessQuery.Edit_business();
  const { mutateAsync: addBusiness } = useBusinessQuery.Add_business();
  const { data: districtList } = useCommonQuery.Get_district(stateId);

  // ── Category + subcategory lookup (same source as CategoryModal / Admin Panel) ──
  const { data: allCategories } = useCommonQuery.Get_category({ search: "", limit: 2000, offset: 0 });
  const { data: allSubCategories } = useCommonQuery.Get_subcategory({
    id: categoryId || undefined,
    search: "",
    limit: 2000,
    offset: 0,
  });

  // ── Load profile data ──
  useEffect(() => {
    if (profileData) {
      setName(profileData.data.name || "");
      setMobile(profileData.data.mobile || "");
      setProfileImage(profileData.data.image || "");
    }
  }, [profileData]);

  // ── Load business data ──
  useEffect(() => {
    const biz = businessDetail?.data || businessDetail || businessList?.data?.results?.[0];
    if (!biz?.id) return;
    setBusinessId(biz.id);
    setBusinessName(biz.name || "");
    setBusinessType(biz.type?.toLowerCase() || "");
    setCategoryId(biz.category || "");
    setCategoryName(biz.category_name || "");
    setCategoryImage(biz.category_image || "");
    setSubCategoryId(biz.sub_category || "");
    setSubCategoryName(biz.sub_category_name || "");
    setSubCategoryImage(biz.sub_category_image || "");
    setDistrict(biz.district || "");
    setStateId(biz.state || 1);
    setPostOffice(biz.post_office || "");
    setLocationInfo(biz.location_info || "");
    if (biz.location_info) {
      setLocationDisplay(biz.location_info);
      setLocationInfo(biz.location_info);
    }
    if (biz.status === "Pending") setShowPending(true);
  }, [businessDetail, businessList]);

  // ── Resolve category image from common API (same source as Admin Panel / CategoryModal) ──
  // This runs whenever categoryId changes and fills in the image if the business
  // endpoint didn’t return it (list endpoint often omits category_image).
  useEffect(() => {
    if (!categoryId || !allCategories?.data?.results) return;
    const match = allCategories.data.results.find(
      (c) => String(c.id) === String(categoryId)
    );
    if (match) {
      setCategoryName(match.name || "");
      if (match.image) setCategoryImage(match.image);
    }
  }, [categoryId, allCategories]);

  // ── Resolve subcategory image from common API ──
  useEffect(() => {
    if (!subCategoryId || !allSubCategories?.data?.results) return;
    const match = allSubCategories.data.results.find(
      (s) => String(s.id) === String(subCategoryId)
    );
    if (match) {
      setSubCategoryName(match.name || "");
      if (match.image) setSubCategoryImage(match.image);
    }
  }, [subCategoryId, allSubCategories]);

  // ── Avatar upload ──
  const handleAvatarClick = () => {
    if (window.FileChannel) {
      window.fileCallback = async (fileData) => {
        try {
          setUploadingPhoto(true);
          let file = parseFlutterFile(fileData);
          if (file) {
            const url = await uploadFileToS3(file);
            setProfileImage(url);
          }
        } catch (e) {
          console.error(e);
        } finally {
          setUploadingPhoto(false);
        }
      };
      window.FileChannel.postMessage("chooseFile");
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingPhoto(true);
      const url = await uploadFileToS3(file);
      setProfileImage(url);
    } catch (err) {
      console.error(err);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ── Category modal callbacks ──
  const handleCategorySelect = (data) => {
    setCategoryModal(false);
    setCategoryId(data.id || "");
    setCategoryName(data.name || "");
    setCategoryImage(data.image || "");
    // reset subcategory when category changes
    setSubCategoryId("");
    setSubCategoryName("");
    setSubCategoryImage("");
  };

  const handleSubCategorySelect = (data) => {
    setSubCategoryModal(false);
    setSubCategoryId(data.id || "");
    setSubCategoryName(data.name || "");
    setSubCategoryImage(data.image || "");
    setSubCategoryError("");
  };

  const handleLocationSelect = (place) => {
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    setLocationFilter("latitude", lat);
    setLocationFilter("longitude", lng);
    setLocationFilter("locationName", place.name);
    setLocationInfo(place.name);
    setLocationDisplay(place.name);
  };

  // ── Save ──
  const handleSave = async () => {
    if (!name.trim() && !businessName.trim()) {
      setNameError("Name is required");
      return;
    }
    if (categoryId && !subCategoryId) {
      setSubCategoryError("Sub Category is required");
      return;
    }
    setSaving(true);
    try {
      // Update personal profile — only send changed/present fields
      const profilePayload = {};
      if (name.trim()) profilePayload.name = name.trim();
      if (profileImage) profilePayload.image = profileImage;
      const loc = locationInfo || locationData?.locationName || locationDisplay;
      if (loc) profilePayload.location = loc;
      await updateProfile(profilePayload);

      // Update or create business
      if (businessId) {
        const biz = businessDetail?.data || businessDetail;
        const payload = {
          id: businessId,
          name: businessName || name,
          type: businessType,
          category: categoryId,
          sub_category: subCategoryId,
          district: district,
          location_info: locationInfo || locationDisplay,
        };
        const carry = ["mobile_number","about_us","services_offered","pin_code","state","latitude","longitude","image","banner_image","whatsapp_number","facebook","instagram","youtube","website","google_location_link","google_review_link"];
        carry.forEach(k => { if (biz?.[k]) payload[k] = biz[k]; });
        await editBusiness(payload);
      } else if (categoryId && businessType) {
        await addBusiness({
          name: name.trim(),
          mobile_number: mobile,
          type: businessType,
          category: categoryId,
          sub_category: subCategoryId || undefined,
          district: district || undefined,
          location_info: locationInfo || locationDisplay || undefined,
          latitude: locationData?.latitude || 0,
          longitude: locationData?.longitude || 0,
        });
        setShowPending(true);
      }

      setShowToast(true);
      setTimeout(() => setShowToast(false), 2800);
      queryClient.invalidateQueries("businessList");
      queryClient.invalidateQueries("businessDetails");
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ep-root">
      {/* ── Top bar ── */}
      <div className="ep-topbar">
        <ArrowBackIcon
          onClick={() => history.push("/my-profile")}
          sx={{ color: "#0E3A53", cursor: "pointer", fontSize: 22 }}
        />
        <Typography className="ep-topbar-title">Edit Profile</Typography>
      </div>

      {/* ── Profile photo strip ── */}
      <div className="ep-profile-strip">
        <div className="ep-strip-avatar-wrap" onClick={handleAvatarClick}>
          {uploadingPhoto ? (
            <div style={{
              width: 80, height: 80, borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <CircularProgress size={28} sx={{ color: "#fff" }} />
            </div>
          ) : (
            <Avatar
              src={profileImage || "/images/user-img.png"}
              className="ep-strip-avatar"
              sx={{ width: 80, height: 80 }}
            />
          )}
          <div className="ep-strip-cam">
            <CameraAltIcon sx={{ fontSize: 14, color: "#0E3A53" }} />
          </div>
        </div>
        <Typography className="ep-strip-name">{businessName || name || "Your Name"}</Typography>
        <Typography className="ep-strip-mobile">{mobile}</Typography>
      </div>

      {/* hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="ep-file-input"
        onChange={handleFileChange}
      />

      {/* ── Form body ── */}
      <div style={{ padding: "14px 14px 100px" }}>

        {/* ── Personal Info card ── */}
        <div className="ep-card">
          <Typography className="ep-card-label">
            <PersonIcon /> Personal Information
          </Typography>

          {/* Name */}
          <div className="ep-field">
            <Typography className="ep-field-label">Full Name *</Typography>
            <div className="ep-input-wrap">
              <PersonIcon className="ep-input-icon" sx={{ color: "#6366f1 !important" }} />
              <input
                className="ep-input ep-input-icon-pad"
                placeholder="Enter your full name"
                value={businessName || name}
                onChange={(e) => { setBusinessName(e.target.value); setName(e.target.value); setNameError(""); }}
              />
            </div>
            {nameError && <Typography className="ep-field-error">{nameError}</Typography>}
          </div>

          {/* Mobile — read only */}
          <div className="ep-field">
            <Typography className="ep-field-label">Registered Mobile</Typography>
            <div className="ep-input-wrap">
              <PhoneIcon className="ep-input-icon" sx={{ color: "#10b981 !important" }} />
              <input
                className="ep-input ep-input-icon-pad ep-input-readonly"
                value={mobile}
                readOnly
              />
              <span className="ep-readonly-badge">Verified</span>
            </div>
          </div>
        </div>

        {/* ── Business Info card ── */}
        <div className="ep-card">
          <Typography className="ep-card-label">
            <StoreIcon /> Business Information
          </Typography>

          {/* Type */}
          <div className="ep-field">
            <Typography className="ep-field-label">Business Type</Typography>
            <div className="ep-input-wrap">
              <StoreIcon className="ep-input-icon" sx={{ color: "#f59e0b !important" }} />
              <select
                className="ep-select ep-input-icon-pad"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
              >
                <option value="">Select Type</option>
                <option value="shop">Shop</option>
                <option value="service">Service</option>
              </select>
            </div>
          </div>

          {/* Category */}
          <div className="ep-field">
            <Typography className="ep-field-label">Business Category</Typography>
            <div
              className="ep-tap-row"
              onClick={() => setCategoryModal(true)}
            >
              {categoryImage ? (
                <img src={categoryImage} alt="" className="ep-cat-img" />
              ) : (
                <div className="ep-cat-img-placeholder">
                  <CategoryIcon sx={{ fontSize: 30, color: "#a0bfce" }} />
                </div>
              )}
              <span className={`ep-tap-value ${!categoryName ? "ep-tap-placeholder" : ""}`}>
                {categoryName || "Select Category"}
              </span>
              <KeyboardArrowDownIcon sx={{ color: "#a0bfce", fontSize: 20 }} />
            </div>
          </div>

          {/* Sub Category */}
          <div className="ep-field">
            <Typography className="ep-field-label">Sub Category</Typography>
            <div
              className="ep-tap-row"
              onClick={() => setCategoryModal(false) || setSubCategoryModal(true)}
            >
              {subCategoryImage ? (
                <img src={subCategoryImage} alt="" className="ep-cat-img" />
              ) : (
                <div className="ep-cat-img-placeholder">
                  <CategoryIcon sx={{ fontSize: 30, color: "#a0bfce" }} />
                </div>
              )}
              <span className={`ep-tap-value ${!subCategoryName ? "ep-tap-placeholder" : ""}`}>
                {subCategoryName || "Select Sub Category"}
              </span>
              <KeyboardArrowDownIcon sx={{ color: "#a0bfce", fontSize: 20 }} />
            </div>
            {subCategoryError && <Typography className="ep-field-error">{subCategoryError}</Typography>}
          </div>
        </div>

        {/* ── Address card ── */}
        <div className="ep-card">
          <Typography className="ep-card-label">
            <LocationOnIcon /> Address Information
          </Typography>

          {/* District */}
          <div className="ep-field">
            <Typography className="ep-field-label">District</Typography>
            <div className="ep-input-wrap">
              <LocationCityIcon className="ep-input-icon" sx={{ color: "#0ea5e9 !important" }} />
              <select
                className="ep-select ep-input-icon-pad"
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
              >
                <option value="">Select District</option>
                {districtList?.data?.results?.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Location */}
          <div className="ep-field">
            <Typography className="ep-field-label">Location</Typography>
            <div className="ep-input-wrap">
              <LocationOnIcon className="ep-input-icon" sx={{ color: "#ef4444 !important" }} />
              <ReactGoogleAutocomplete
                key={locationDisplay}
                apiKey={API_KEY}
                className="ep-input ep-input-icon-pad"
                placeholder="Search location"
                defaultValue={locationDisplay}
                options={{
                  types: ["neighborhood", "locality", "sublocality", "postal_code", "point_of_interest"],
                  componentRestrictions: { country: "in" },
                  fields: ["address_components", "geometry", "name", "place_id"],
                }}
                onPlaceSelected={handleLocationSelect}
                style={{ border: "none", outline: "none", background: "transparent", width: "100%", height: "100%", fontSize: 14, fontFamily: "inherit", color: "#1a2e3b" }}
              />
            </div>
          </div>
        </div>

      </div>

      {/* ── Pending banner ── */}
      {showPending && (
        <div style={{
          margin: "0 14px 16px",
          background: "linear-gradient(135deg, #fff8e1, #fff3cd)",
          border: "1.5px solid #f59e0b",
          borderRadius: 14,
          padding: "14px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>⏳</span>
            <span style={{ fontWeight: 700, color: "#92400e", fontSize: 14 }}>
              Business Under Verification
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "#78350f", lineHeight: 1.6 }}>
            Your business is being reviewed and will be approved within <strong>2 working days</strong>.
          </p>
          <a
            href="https://wa.me/917293838383"
            target="_blank"
            rel="noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#25D366", color: "#fff",
              padding: "8px 16px", borderRadius: 50,
              textDecoration: "none", fontWeight: 600, fontSize: 13,
              alignSelf: "flex-start",
            }}
          >
            <span style={{ fontSize: 16 }}>💬</span> Contact on WhatsApp
          </a>
        </div>
      )}

      {/* ── Save bar ── */}
      <div className="ep-save-bar">
        <Button
          className="ep-cancel-btn"
          onClick={() => history.push("/my-profile")}
        >
          Cancel
        </Button>
        <Button
          className="ep-save-btn"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
        >
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>

      {/* ── Success toast ── */}
      {showToast && (
        <div className="ep-toast">
          <CheckCircleIcon sx={{ fontSize: 16, marginRight: "6px", verticalAlign: "middle" }} />
          Profile updated successfully
        </div>
      )}

      {/* ── Modals ── */}
      {otpOpen && (
        <OtpModal open={otpOpen} handleClose={() => setOtpOpen(false)} mobile={mobile} />
      )}
      {categoryModal && (
        <CategoryModal
          open={categoryModal}
          setOpen={setCategoryModal}
          handleClose={handleCategorySelect}
          categoryId={categoryId}
        />
      )}
      {subCategoryModal && (
        <SubCategoryModal
          open={subCategoryModal}
          setOpen={setSubCategoryModal}
          handleClose={handleSubCategorySelect}
          categoryId={categoryId}
          subCategoryId={subCategoryId}
        />
      )}
    </div>
  );
};

// ── Flutter file bridge helper ──
function parseFlutterFile(fileData) {
  try {
    if (typeof fileData === "object" && fileData?.fileBase64) {
      const { fileName, fileBase64, fileType } = fileData;
      const base64 = fileBase64.includes("base64,") ? fileBase64.split("base64,")[1] : fileBase64;
      const bytes = atob(base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      return new File([arr], fileName || `photo_${Date.now()}.jpg`, { type: fileType || "image/jpeg" });
    }
    if (typeof fileData === "string" && fileData.includes("base64")) {
      const m = fileData.match(/^data:([^;]+);base64,(.+)$/);
      if (m) {
        const bytes = atob(m[2]);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        return new File([arr], `photo_${Date.now()}.jpg`, { type: m[1] });
      }
    }
  } catch (e) { console.error(e); }
  return null;
}

export default Profile;
